import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { AdminUsersPanel } from "@/pages/AdminUsers";
import AdminPlansPanel from "@/components/AdminPlansPanel";
import AdminCampaignPanel from "@/components/AdminCampaignPanel";
import AdminSupportPanel from "@/components/AdminSupportPanel";
import { Users, Package, Image as ImageIcon, Upload, Loader2, TrendingUp, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

function LoginBgPanel() {
  const [bg, setBg] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setBg(r.data?.login_background || "")).catch(() => {}); }, []);
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Obrázok je príliš veľký (max 3 MB)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setSaving(true);
      try { await api.put("/settings", { login_background: reader.result }); setBg(reader.result); toast.success("Pozadie uložené"); }
      catch { toast.error("Nepodarilo sa uložiť"); }
      finally { setSaving(false); }
    };
    reader.readAsDataURL(f);
  };
  const remove = async () => {
    setSaving(true);
    try { await api.put("/settings", { login_background: "" }); setBg(""); toast.success("Pozadie odstránené"); }
    catch { toast.error("Nepodarilo sa odstrániť"); }
    finally { setSaving(false); }
  };
  return (
    <div className="card-surface space-y-4 p-6" data-testid="login-bg-panel">
      <h3 className="flex items-center gap-2 font-heading text-lg font-bold uppercase"><ImageIcon className="h-5 w-5 text-primary" /> Prihlasovacie pozadie</h3>
      <p className="text-sm text-muted-foreground">Nahrajte vlastný obrázok na prihlasovaciu stránku. Ak žiadny nenahráte, použije sa predvolené pozadie.</p>
      <div className="flex flex-wrap items-center gap-4">
        {bg && <img src={bg} alt="pozadie" className="h-20 w-32 rounded border border-border object-cover" data-testid="login-bg-preview" />}
        <label className="flex min-h-[48px] cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-4 text-sm text-muted-foreground hover:border-primary" data-testid="login-bg-label">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Nahrať pozadie
          <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="login-bg-input" />
        </label>
        {bg && <Button variant="ghost" size="sm" onClick={remove} disabled={saving} data-testid="login-bg-remove">Odstrániť</Button>}
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  return (
    <div data-testid="admin-panel-page">
      <PageHeader title="Administrácia" subtitle="Správa účtov, kreditov, balíčkov a základné úpravy aplikácie" />
      <Tabs defaultValue="ucty">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-muted/60 p-1">
          <TabsTrigger value="ucty" className="min-h-[40px]" data-testid="admin-tab-users"><Users className="mr-2 h-4 w-4" />Účty</TabsTrigger>
          <TabsTrigger value="balicky" className="min-h-[40px]" data-testid="admin-tab-plans"><Package className="mr-2 h-4 w-4" />Balíčky a limity</TabsTrigger>
          <TabsTrigger value="vzhlad" className="min-h-[40px]" data-testid="admin-tab-appearance"><ImageIcon className="mr-2 h-4 w-4" />Vzhľad</TabsTrigger>
          <TabsTrigger value="kampan" className="min-h-[40px]" data-testid="admin-tab-campaign"><TrendingUp className="mr-2 h-4 w-4" />Kampaň</TabsTrigger>
          <TabsTrigger value="support" className="min-h-[40px]" data-testid="admin-tab-support"><LifeBuoy className="mr-2 h-4 w-4" />Support</TabsTrigger>
        </TabsList>
        <TabsContent value="ucty"><div className="card-surface p-6"><AdminUsersPanel /></div></TabsContent>
        <TabsContent value="balicky"><div className="card-surface p-6"><AdminPlansPanel /></div></TabsContent>
        <TabsContent value="vzhlad"><LoginBgPanel /></TabsContent>
        <TabsContent value="kampan"><div className="card-surface p-6"><AdminCampaignPanel /></div></TabsContent>
        <TabsContent value="support"><div className="card-surface p-6"><AdminSupportPanel /></div></TabsContent>
      </Tabs>
    </div>
  );
}
